import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ButtonV2 } from '@asigna/kit';
import cx from 'classnames';
import { useReadOnlyWarning, useSidebarState } from 'hooks';
import useAddress from 'hooks/use-address';
import { useBreakpoints } from 'hooks/use-breakpoints';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import AsignaLogo from 'images/asigna-logo.svg';
import { EXTENSION_LINK } from 'services/constants';
import {
  authenticateExtension,
  disconnectExtension,
  getExtProvider,
} from 'services/safeExtensionService';
import {
  useAppsIframe,
  useArchivedSafes,
  useCurrentSafe,
  useExtensionState,
  usePendingSafes,
  useSafes,
  useSafesBalances,
  useTokenStorage,
} from 'store';
import { IClaimMultiSignWallet, IMultiSignWallet } from 'types';
import { isPendingSafe } from 'utils/helper';
import { getStorageKeysForNetwork } from 'utils/LocalstorageUtils';

import { ConnectWalletButton } from '../ConnectWalletButton';
import { MenuLst } from '../MenuLst';
import { SafeCard } from '../SafeCard';
import { SelectedSafeCard } from '../SelectedSafeCard';
import { SkeletonSelectedSafeCard } from '../SkeletonSelectedSafeCard';

import arrowIcon from './util/images/arrow.svg';
import swipeBtnIcon from './util/images/button.svg';
import emptyIcon from './util/images/empty.svg';
import greenCheck from './util/images/green.svg';
import { useLoadSafesBalances } from './util/useLoadSafesBalances';
import { useRefreshCurrentSafeBalance } from './util/useRefreshCurrentSafeBalance';
import { useSidebarContent } from './util/useSidebarContent';

import s from './index.module.scss';

const { getToken } = useTokenStorage();

export const SidebarContent: React.FC<{
  handleBreakIframe: () => void;
  handleSidebarHidden: () => void;
  handleReceiveBtcDialogOpen: () => void;
  handleMobileMenuOpen: (val: boolean) => void;
}> = ({
  handleBreakIframe,
  handleSidebarHidden,
  handleReceiveBtcDialogOpen,
  handleMobileMenuOpen,
}) => {
  const history = useHistory();
  const location = useLocation();
  const isInvite = location.pathname.startsWith('/invite/');

  const {
    isDropdownOpen,
    setDropdownOpen,
    isAnimating,
    setIsAnimating,
    isIframeSidebarOpen,
    setIsIframeAnimating,
  } = useSidebarContent();
  const { currentSafe } = useCurrentSafe();
  const { onMobileZone } = useBreakpoints();
  const { setCurrentSafeId, currentParentSafeId, setCurrentParentSafeId, currentSafeId } =
    useSidebarState();
  const { setExtensionConnected, extensionConnected } = useExtensionState();
  const walletAccount = useAddress();
  const [network] = useNetwork();
  const { safes } = useSafes();
  const { pendingSafes } = usePendingSafes();
  const { archivedSafes } = useArchivedSafes();
  const { fetchSafeData } = useSafe();
  const { safesBalances } = useSafesBalances();
  const { isIframeRun } = useAppsIframe();
  const isReadOnlyMode = Boolean(useReadOnlyWarning());
  const sidebarRef = useRef<HTMLDivElement>(null);

  useRefreshCurrentSafeBalance(currentSafe);
  useLoadSafesBalances();

  const sortedSafes = useMemo(
    () =>
      [...safes]
        .filter((safe) => !safe.parrentId)
        .sort((a, b) => {
          const balanceA = Number(safesBalances && safesBalances[a.address]);
          const balanceB = Number(safesBalances && safesBalances[b.address]);

          if (balanceB !== balanceA) {
            return balanceB - balanceA;
          }

          const dateA = new Date(a.create_time).getTime();
          const dateB = new Date(b.create_time).getTime();

          return dateB - dateA;
        }),
    [safes, safesBalances]
  );

  const sortedPendingSafes = useMemo(
    () =>
      [...pendingSafes].sort((a, b) => {
        const dateA = new Date(a.create_time).getTime();
        const dateB = new Date(b.create_time).getTime();

        return dateB - dateA;
      }),
    [pendingSafes]
  );

  // const safeById = [...safes, ...pendingSafes].find((s) => s._id === currentSafeId);
  const safeById = useMemo(() => {
    return (
      safes.find((safe) => safe._id === currentSafeId) ||
      pendingSafes.find((safe) => safe._id === currentSafeId) ||
      archivedSafes.find((safe) => safe._id === currentSafeId) ||
      (isReadOnlyMode || currentSafeId === currentSafe?._id ? currentSafe : null)
    );
  }, [currentSafeId, safes, pendingSafes, archivedSafes, currentSafe, isReadOnlyMode]);

  useEffect(() => {
    if (!safeById?._id || !safeById?.address) return;
    fetchSafeData(safeById._id).then();
  }, [safeById?._id]);

  const handleSelectSafe = (
    safe: IClaimMultiSignWallet | IMultiSignWallet,
    isSubAccount?: boolean
  ) => {
    const sidebar = sidebarRef.current;
    if (!isSubAccount) {
      setDropdownOpen(false);

      if (sidebar) {
        setTimeout(
          () =>
            sidebar.scrollTo({
              top: 0,
              behavior: 'smooth',
            }),
          420
        );
      }
    } else {
      if (sidebar) {
        sidebar.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
    handleBreakIframe();
    onMobileZone && handleMobileMenuOpen(false);
    setCurrentSafeId(safe._id);
    setCurrentParentSafeId(safe.parrentId || safe._id);
    history.push(isPendingSafe(safe) ? `/invite/${safe._id}` : `/vault/${safe.address}/dashboard`);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      setIsAnimating(true);
    } else {
      const timeout = setTimeout(() => setIsAnimating(false), 420);

      return () => clearTimeout(timeout);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isIframeSidebarOpen) {
      setIsIframeAnimating(true);
    } else {
      const timeout = setTimeout(() => setIsIframeAnimating(false), 420);

      return () => clearTimeout(timeout);
    }
  }, [isIframeSidebarOpen]);

  const authExt = useCallback(
    (isSilent?: boolean) => {
      const token = getToken() || '';
      const storageKeys = getStorageKeysForNetwork();
      const mainnetAddress = localStorage.getItem(storageKeys.mainnetAddress);
      const testnetAddress = localStorage.getItem(storageKeys.testnetAddress) || '';
      authenticateExtension({
        token,
        address: mainnetAddress || '',
        testnetAddress,
        network,
        isSilent,
      });
      setExtensionConnected(true);
    },
    [walletAccount, network]
  );

  const onConnect = () => {
    authExt();
  };

  return (
    <div
      className={cx(
        'flex flex-col w-[266rem] pl-20 sm:pl-24 pr-20 sm:pr-12 sm:mr-12 py-16 items-start gap-16 shrink-0 relative h-[100vh] overflow-y-scroll no-scrollbar',
        s.sidebar
      )}
    >
      <div className="flex w-full items-center justify-between">
        <a href="/" className="flex py-[5px]">
          <img src={AsignaLogo} width={80} height={30} alt="Asigna" />
        </a>
        <ConnectWalletButton />
      </div>
      {safeById ? (
        <SelectedSafeCard
          safe={safeById}
          handleDropdownClick={() => setDropdownOpen((prevState) => !prevState)}
          handleReceiveButton={() => {
            if (!isPendingSafe(safeById)) {
              handleReceiveBtcDialogOpen();
            }
          }}
          handleMobileMenuOpen={(val) => {
            if (!isPendingSafe(safeById)) {
              handleMobileMenuOpen(val);
            }
          }}
        />
      ) : (
        <div className={isInvite ? 'h-[225px]' : 'h-[128px]'}>
          <SkeletonSelectedSafeCard isInvite={isInvite} />
        </div>
      )}

      <ButtonV2
        color="secondary"
        className="w-full flex gap-[6px] items-center border-[.5px]"
        size="md"
        onClick={() => {
          if (!getExtProvider()) {
            window.open(EXTENSION_LINK, `_blank`);

            return;
          }
          if (extensionConnected) {
            disconnectExtension();
            setExtensionConnected(false);
          } else onConnect();
        }}
      >
        <div className="w-full flex gap-[6px] items-center">
          {getExtProvider() && (
            <span className="text-r12">
              {extensionConnected ? 'Connected' : 'Connect extension'}
            </span>
          )}
          {!getExtProvider() && <span className="text-r12">Install extension</span>}
          {extensionConnected && <img src={greenCheck} alt="" />}
        </div>
      </ButtonV2>

      <MenuLst
        handleBreakIframe={() => {
          handleBreakIframe();
          onMobileZone && handleMobileMenuOpen(false);
        }}
      />

      <div
        className="fixed inset-0 bg-black-0"
        style={{
          zIndex: isAnimating ? 10 : -9999,
          visibility: isAnimating ? 'visible' : 'hidden',
          opacity: isDropdownOpen ? 0.7 : 0,
          transition: 'opacity 425ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onClick={() => setDropdownOpen(false)}
      />

      <div
        ref={sidebarRef}
        className="fixed top-0 left-0 flex flex-col gap-16 self-stretch h-[100vh] overflow-y-scroll border-r-[.5px] border-black-4 bg-black-1 w-full max-w-[92vw] sm:max-w-[360px] px-16 sm:px-24 py-24"
        style={{
          left: isDropdownOpen ? '0' : onMobileZone ? '-92vw' : '-370px',
          zIndex: isAnimating ? 50 : 0,
          transition: 'left 0.4s ease-in-out',
        }}
      >
        {[...sortedSafes, ...sortedPendingSafes, ...archivedSafes].length === 0 ? (
          <div className="flex flex-col items-center justify-between gap-12 mb-8">
            <h2 className="sm:text-r24 text-r16 text-white text-left flex w-full">Your Vaults</h2>
            <div className="w-48 h-48 rounded-[24px] border border-white-10 flex items-center justify-center shrink-0 mb-4 mt-44">
              <img src={emptyIcon} alt="" />
            </div>
            <h3 className="text-m16 text-white">No Vaults</h3>
            <span className="text-regular text-center text-r14 mb-4">
              You don’t have any vaults. Please create new
            </span>
            <ButtonV2
              size="md"
              className="w-full"
              onClick={() => {
                setDropdownOpen(false);
                handleBreakIframe();
                onMobileZone && handleMobileMenuOpen(false);
                history.push('/createBitcoinMultisig');
              }}
            >
              Create new
            </ButtonV2>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-8 mb-8">
            <h2 className="sm:text-r24 text-r16 text-white">Your Vaults</h2>
            <ButtonV2
              size="md"
              onClick={() => {
                onMobileZone && handleMobileMenuOpen(false);
                history.push('/createSafe');
              }}
            >
              Create new
            </ButtonV2>
          </div>
        )}
        {[...sortedSafes, ...sortedPendingSafes, ...archivedSafes]
          .filter((safe) => safe._id === currentParentSafeId)
          .map((safe) => (
            <SafeCard
              key={safe._id}
              safe={safe}
              onClick={handleSelectSafe}
              onClose={() => {
                setDropdownOpen(false);
              }}
            />
          ))}
        {[...sortedSafes, ...sortedPendingSafes]
          .filter((safe) => safe._id !== currentParentSafeId)
          .map((safe) => (
            <SafeCard
              key={safe._id}
              safe={safe}
              onClick={handleSelectSafe}
              onClose={() => {
                setDropdownOpen(false);
              }}
            />
          ))}
      </div>

      {isIframeRun && !onMobileZone && (
        <button
          className="fixed w-[29px] h-[74px] top-1/2 transform -translate-y-1/2 left-[278px] z-20"
          onClick={handleSidebarHidden}
        >
          <img src={swipeBtnIcon} width={29} height={74} className="z-10" alt="" />
          <img
            src={arrowIcon}
            width={20}
            height={20}
            className="absolute z-20 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-180"
            alt="arrow"
          />
        </button>
      )}
    </div>
  );
};
